import { InfoIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
  Stack,
  Tooltip,
} from '@chakra-ui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { BigNumber } from 'bignumber.js';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { FormErrors } from '@blockpulse3/data/shared';
import { useGetFundraisingQuery } from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency, getNearestMultiple } from '@blockpulse3/helpers';
import { ErrorMessage, ErrorQueryCard } from '@blockpulse3/ui/commons';

import { getPrivateFundraisingParameters } from '../../../utils';
import { schema } from './schema';
import { IFundraisingLegalSubscriberInformations } from './types';

type Props = {
  defaultValues: IFundraisingLegalSubscriberInformations;
  onSubmit: (data: IFundraisingLegalSubscriberInformations) => void;
};

export function LegalSubscriberInformationsForm({ defaultValues, onSubmit }: Props): JSX.Element {
  const i18nAlertAmount = useTranslations('AlertAmount');
  const i18nLegalSubscriberInformationsForm = useTranslations('LegalSubscriberInformationsForm');

  const { fundraisingId = '' } = useParams();

  /* ** Track amount touched state ** */
  const [isAmountTouched, setIsAmountTouched] = useState<boolean>(false);

  const { data, loading, error } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    skip: !fundraisingId,
  });

  const fundraising = data?.fundraising;
  const { pricePerShare } = getPrivateFundraisingParameters(fundraising);

  const { register, formState, handleSubmit, watch, setError } =
    useForm<IFundraisingLegalSubscriberInformations>({
      defaultValues,
      resolver: yupResolver(schema),
    });

  if (loading) {
    //TODO: remove spinner
    return <Spinner />;
  }

  if (error) {
    return <ErrorQueryCard />;
  }

  /* ** Compute amount to a multiple of the pricePerShare ** */
  const amount = new BigNumber(watch('amount'));
  const fixedAmount = getNearestMultiple(amount, pricePerShare);
  const isFixed = !amount.isEqualTo(fixedAmount);
  const isNull = fixedAmount.isZero() || fixedAmount.isNaN();
  const showAlert = (isFixed || isNull) && isAmountTouched;

  const handleFormSubmit: SubmitHandler<IFundraisingLegalSubscriberInformations> = (data): void => {
    /* ** Check for a new condition on submit. Forbid a 0 value rounded amount ** */
    const nbAmount = fixedAmount.toNumber();
    if (nbAmount === 0) {
      setError('amount', { type: 'custom', message: FormErrors.PositiveFormat });
    } else {
      onSubmit({ ...data, amount: nbAmount });
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLFormElement>): void => {
    /* ** Avoid onBlur formState.touchedFields update ** */
    if (e.target.id === 'amount') {
      setIsAmountTouched(true);
    }
  };

  return (
    <form onChange={handleFormChange} onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing="8">
        <Stack spacing="4">
          <FormControl isInvalid={!!formState.errors.email}>
            <FormLabel htmlFor="email">{i18nLegalSubscriberInformationsForm('Email')}</FormLabel>
            <Input id="email" type="string" {...register('email')} />
            <ErrorMessage error={formState.errors.email} />
          </FormControl>
          <Stack direction={{ base: 'column', md: 'row' }} spacing="4">
            <FormControl isInvalid={!!formState.errors.name}>
              <FormLabel htmlFor="name">{i18nLegalSubscriberInformationsForm('Name')}</FormLabel>
              <Input id="name" type="string" {...register('name')} />
              <ErrorMessage error={formState.errors.name} />
            </FormControl>
            <FormControl isInvalid={!!formState.errors.identificationNumber}>
              <HStack alignItems="flex-start" spacing="0">
                <FormLabel htmlFor="identificationNumber">
                  {i18nLegalSubscriberInformationsForm('IdentificationNumber')}
                </FormLabel>
                <Tooltip
                  hasArrow
                  label={i18nLegalSubscriberInformationsForm('Tooltip.IdentificationNumber')}
                  placement="top"
                >
                  <Icon as={InfoIcon} color="gray.500" />
                </Tooltip>
              </HStack>
              <Input
                id="identificationNumber"
                type="string"
                {...register('identificationNumber')}
              />
              <ErrorMessage error={formState.errors.identificationNumber} />
            </FormControl>
          </Stack>
          <FormControl isInvalid={!!formState.errors.amount}>
            <FormLabel htmlFor="amount">{i18nLegalSubscriberInformationsForm('Amount')}</FormLabel>
            <InputGroup>
              <Input id="amount" step="0.01" type="number" {...register('amount')} />
              <InputRightAddon>€</InputRightAddon>
            </InputGroup>
            <ErrorMessage error={formState.errors.amount} />
            {showAlert && (
              <Alert mt="3" status="warning">
                <AlertIcon as={ExclamationIcon} />
                <Stack spacing="1">
                  <AlertTitle>{i18nAlertAmount('Title')}</AlertTitle>
                  <AlertDescription>
                    {isNull
                      ? i18nAlertAmount('DescriptionZero')
                      : i18nAlertAmount('Description', {
                          fixedAmount: formatNumberCurrency(fixedAmount),
                        })}
                  </AlertDescription>
                </Stack>
              </Alert>
            )}
          </FormControl>
          <FormControl isInvalid={!!formState.errors.admissionFees}>
            <FormLabel htmlFor="admissionFees">
              {i18nLegalSubscriberInformationsForm('EntranceFee')}
            </FormLabel>
            <InputGroup>
              <Input id="admissionFees" step="0.01" type="number" {...register('admissionFees')} />
              <InputRightAddon>%</InputRightAddon>
            </InputGroup>
            <ErrorMessage error={formState.errors.admissionFees} />
          </FormControl>
        </Stack>
        <Button data-cy="validate-subscriber" type="submit" w="full">
          {i18nLegalSubscriberInformationsForm('AddSubscriber')}
        </Button>
      </Stack>
    </form>
  );
}

export type LegalSubscriberInformationsFormProps = Props;
